import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AttachmentSearchGroup, Note, NoteListResponse } from "../domains/note";

export interface NoteListParams {
  q?: string;
  object_type?: string;
  /** Filtrer sur plusieurs object_types en parallèle (paramètres répétés) */
  object_types?: string[];
  object_id?: string;
  is_pinned?: boolean;
  limit?: number;
  skip?: number;
}

@Injectable({
  providedIn: "root",
})
export class NoteService {
  private url = environment.apiUrl + "/v1/notes";

  private notesSubject = new BehaviorSubject<Note[]>([]);
  public notes$ = this.notesSubject.asObservable();
  /** Émis après création / modification / suppression : compteurs à rafraîchir (EP-1120) */
  readonly notesChanged$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  // ==========================================================================
  // READ
  // ==========================================================================

  /**
   * Liste les notes avec filtres et pagination.
   * Retourne la reponse paginee complete { data, total, limit, skip }.
   */
  getAll(params?: NoteListParams): Observable<NoteListResponse> {
    let httpParams = new HttpParams();

    if (params?.q) {
      httpParams = httpParams.set("q", params.q);
    }
    if (params?.object_types?.length) {
      for (const t of params.object_types) {
        httpParams = httpParams.append("object_type", t);
      }
    } else if (params?.object_type) {
      httpParams = httpParams.set("object_type", params.object_type);
    }
    if (params?.object_id) {
      httpParams = httpParams.set("object_id", params.object_id);
    }
    if (params?.limit !== undefined) {
      httpParams = httpParams.set("limit", params.limit.toString());
    }
    if (params?.skip !== undefined) {
      httpParams = httpParams.set("skip", params.skip.toString());
    }
    if (params?.is_pinned !== undefined) {
      httpParams = httpParams.set("is_pinned", params.is_pinned.toString());
    }

    return this.http.get<NoteListResponse>(this.url, { params: httpParams }).pipe(
      map((response) => ({
        ...response,
        data: Note.createInstances(response.data),
      })),
      tap((response) => {
        if (!params?.skip || params.skip === 0) {
          this.notesSubject.next(response.data);
        } else {
          const current = this.notesSubject.getValue();
          this.notesSubject.next([...current, ...response.data]);
        }
      }),
    );
  }

  /**
   * Recupere une note par son ID.
   */
  getById(id: string): Observable<Note> {
    return this.http
      .get<{ data: Note }>(this.url + "/" + id)
      .pipe(map((response) => Note.createInstance(response.data)));
  }

  // ==========================================================================
  // WRITE
  // ==========================================================================

  /** Nombre de notes d'un objet, sans toucher à la liste partagée notes$ (getAll la remplace) */
  countForObject(objectType: string, objectId: string): Observable<number> {
    const params = new HttpParams()
      .set("object_type", objectType)
      .set("object_id", objectId)
      .set("limit", "1")
      .set("skip", "0");
    return this.http.get<NoteListResponse>(this.url, { params }).pipe(map((response) => response.total || 0));
  }

  /**
   * Cree une nouvelle note.
   */
  create(note: Partial<Note>): Observable<Note> {
    return this.http.post<{ data: Note }>(this.url, note).pipe(
      map((response) => Note.createInstance(response.data)),
      tap((newNote) => {
        const current = this.notesSubject.getValue();
        this.notesSubject.next([newNote, ...current]);
        this.notesChanged$.next();
      }),
    );
  }

  /**
   * Met a jour partiellement une note (PATCH).
   */
  update(id: string, partial: Partial<Note>): Observable<Note> {
    return this.http.patch<{ data: Note }>(this.url + "/" + id, partial).pipe(
      map((response) => Note.createInstance(response.data)),
      tap((updatedNote) => {
        const current = this.notesSubject.getValue();
        const index = current.findIndex((n) => n.note_id === id);
        if (index >= 0) {
          const updated = [...current];
          updated[index] = updatedNote;
          this.notesSubject.next(updated);
        }
        this.notesChanged$.next();
      }),
    );
  }

  /**
   * Supprime (soft delete) une note.
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(this.url + "/" + id).pipe(
      tap(() => {
        const current = this.notesSubject.getValue();
        this.notesSubject.next(current.filter((n) => n.note_id !== id));
        this.notesChanged$.next();
      }),
    );
  }

  /**
   * Restaure une note archivee (admin uniquement).
   */
  restore(id: string): Observable<Note> {
    return this.http.put<{ data: Note }>(this.url + "/" + id + "/restore", {}).pipe(
      map((response) => Note.createInstance(response.data)),
      tap((restoredNote) => {
        const current = this.notesSubject.getValue();
        this.notesSubject.next([restoredNote, ...current]);
      }),
    );
  }

  /**
   * Duplique une note existante (copie côté client).
   */
  duplicate(id: string): Observable<Note> {
    return this.getById(id).pipe(
      switchMap((note) =>
        this.create({
          title: note.title + " (copie)",
          content: note.content,
          is_pinned: false,
          attachments: [...note.attachments],
        }),
      ),
    );
  }

  // ==========================================================================
  // SEARCH ATTACHMENTS
  // ==========================================================================

  /**
   * Recherche des objets rattachables (autocomplete multi-types).
   */
  searchAttachments(query: string, types?: string): Observable<AttachmentSearchGroup[]> {
    let httpParams = new HttpParams().set("q", query);
    if (types) {
      httpParams = httpParams.set("types", types);
    }
    return this.http
      .get<{ data: AttachmentSearchGroup[] }>(this.url + "/search-attachments", { params: httpParams })
      .pipe(map((response) => response.data));
  }
}
